<template>
  <header class="k-navbar">
    <!-- Mobile Menu Toggle -->
    <button
      class="k-navbar__mobile-toggle"
      @click="toggleSidebar"
      aria-label="Toggle sidebar"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>

    <!-- Breadcrumb / Title -->
    <div class="k-navbar__title">
      <span class="k-navbar__route">{{ routeName }}</span>
    </div>

    <!-- Spacer -->
    <div class="k-navbar__spacer"></div>

    <!-- Actions -->
    <div class="k-navbar__actions">
      <!-- Shield Icon -->
      <button
        class="k-navbar__action-btn k-navbar__action-btn--shield"
        @click="handleShieldClick"
        aria-label="Sicherheit"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      </button>

      <!-- Euro Icon -->
      <button
        class="k-navbar__action-btn k-navbar__action-btn--euro"
        type="button"
        aria-label="Finanzen"
        @click="handleEuroClick"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <!-- Euro-Symbol -->
          <path d="M17 5.5a6 6 0 0 0-9.5 2.1A6 6 0 0 0 7 16.5a6 6 0 0 0 9 0" />
          <path d="M7 10h7" />
          <path d="M7 14h6.5" />
        </svg>
      </button>

      <!-- Search Button -->
      <button
        class="k-navbar__action-btn"
        @click="toggleSearch"
        aria-label="Suche"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>

      <!-- Theme Toggle -->
      <theme-toggle />

      <!-- Chat -->
      <navbar-chat />

      <!-- Notifications -->
      <navbar-notifications
        :notifications="notifications"
        @clear="clearNotifications"
      />

      <!-- Profile -->
      <navbar-profile
        :user-name="userName"
        :user-email="userEmail"
        @logout="handleLogout"
      />
    </div>

    <!-- Search Modal -->
    <navbar-search
      :show="showSearch"
      v-model="searchQuery"
      @close="toggleSearch"
    />
  </header>
</template>

<script>
import ThemeToggle from "@/components/ThemeToggle.vue";
import {
  NavbarSearch,
  NavbarNotifications,
  NavbarChat,
  NavbarProfile,
} from "@/components/navbar";
import authStore, { UserRoles } from "@/stores/authStore";

/**
 * TopNavbar - Apple-style top navigation bar
 * @description Modern navbar with search, notifications, theme toggle, and profile
 */
export default {
  name: "TopNavbar",
  components: {
    ThemeToggle,
    NavbarSearch,
    NavbarNotifications,
    NavbarChat,
    NavbarProfile,
  },
  data() {
    return {
      showSearch: false,
      searchQuery: "",
      userName: "Max Mustermann",
      userEmail: "max@example.com",
      notifications: [
        { id: 1, text: "MRR wurde aktualisiert", time: "vor 5 Min" },
        { id: 2, text: "Neuer Investor-Zugang", time: "vor 1 Std" },
        { id: 3, text: "Monatsbericht verfuegbar", time: "Gestern" },
      ],
    };
  },
  computed: {
    /**
     * Current route name, capitalized
     * @returns {String}
     */
    routeName() {
      const { name } = this.$route;
      if (!name) return "Dashboard";
      return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, " ");
    },
  },
  methods: {
    /**
     * Toggles mobile sidebar
     */
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    /**
     * Toggles search modal
     */
    toggleSearch() {
      this.showSearch = !this.showSearch;
    },
    /**
     * Clears all notifications
     */
    clearNotifications() {
      this.notifications = [];
    },
    /**
     * Handles logout
     */
    handleLogout() {
      localStorage.removeItem("auth_token");
      this.$router.push({ name: "login" });
    },
    /**
     * Handles shield icon click
     * @description Navigiert abhängig von der aktuellen Rolle zur passenden
     * Risk-Shield-Route (Entrepreneur oder Investor). Verhindert redundante
     * Navigation, wenn bereits auf der Ziel-Route.
     */
    handleShieldClick() {
      const role = authStore.role;

      if (role === UserRoles.ENTREPRENEUR) {
        // Nur navigieren, wenn nicht bereits auf der Ziel-Route
        if (this.$route.name !== "entrepreneur-risk-shield") {
          this.$router.push({ name: "entrepreneur-risk-shield" }).catch(() => {});
        }
        return;
      }

      if (role === UserRoles.INVESTOR) {
        // Nur navigieren, wenn nicht bereits auf der Ziel-Route
        if (this.$route.name !== "investor-risk-shield") {
          this.$router.push({ name: "investor-risk-shield" }).catch(() => {});
        }
        return;
      }

      // Fallback: Kein klarer Role-Context -> zum Profil leiten
      if (this.$route.name !== "profile") {
        this.$router.push({ name: "profile" }).catch(() => {});
      }
    },

    /**
     * Handles euro icon click
     * @description Navigiert abhängig von der aktuellen Rolle zur passenden
     * Cashradar-Route (Entrepreneur oder Investor). Verhindert redundante
     * Navigation, wenn bereits auf der Ziel-Route.
     */
    handleEuroClick() {
      const role = authStore.role;

      if (role === UserRoles.ENTREPRENEUR) {
        if (this.$route.name !== "entrepreneur-cashradar") {
          this.$router.push({ name: "entrepreneur-cashradar" }).catch(() => {});
        }
        return;
      }

      if (role === UserRoles.INVESTOR) {
        if (this.$route.name !== "investor-cashradar") {
          this.$router.push({ name: "investor-cashradar" }).catch(() => {});
        }
        return;
      }

      // Fallback: Kein klarer Role-Context -> zum Profil leiten
      if (this.$route.name !== "profile") {
        this.$router.push({ name: "profile" }).catch(() => {});
      }
    },
  },
};
</script>
